import React, { useState } from "react";
import { PDFViewer } from "@react-pdf/renderer";
import { useParams } from "react-router-dom";
import useSave from "../../../Hooks/useSave";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import OrdersReportPdf from "./OrdersReportPdf";

// helpers
const formatDate = (d) => d.toISOString().slice(0, 10);

export default function CustomerOrdersReport() {
  const { customerId } = useParams();
  const numericCustomerId = Number(customerId);

  const { fetchGraphql, DialogSave } = useSave();

  const [orders, setOrders] = useState([]);
  const [reportType, setReportType] = useState("weekly");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!customerId || Number.isNaN(numericCustomerId)) {
    return <Typography color="error">Invalid customer ID</Typography>;
  }

  // 🔹 Auto-set dates when report type changes
  const handleReportTypeChange = (_, type) => {
    if (!type) return;

    setReportType(type);

    const today = new Date();

    if (type === "weekly") {
      const from = new Date(today);
      from.setDate(today.getDate() - 7);
      setFromDate(formatDate(from));
      setToDate(formatDate(today));
    }

    if (type === "monthly") {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      setFromDate(formatDate(from));
      setToDate(formatDate(today));
    }

    if (type === "custom") {
      setFromDate("");
      setToDate("");
    }
  };

  const generateReport = async () => {
    setLoading(true);

    const res = await fetchGraphql({
      query: `
        query OrdersReport(
          $customerId: ID!
          $fromDate: String!
          $toDate: String!
        ) {
          ordersReport(
            customerId: $customerId
            fromDate: $fromDate
            toDate: $toDate
          ) {
            orderId
            orderDate
            deliveryDate
            customerName
            productName
            quantity
            price
          }
        }
      `,
      variables: {
        customerId: String(customerId),
        fromDate,
        toDate,
      },
    });

    setLoading(false);

    if (res?.errors?.length) {
      alert(res.errors[0].message);
      return;
    }

    if (!res?.data?.ordersReport?.length) {
      alert("No orders found for selected period");
      return;
    }

    setOrders(res.data.ordersReport);
    setShowPdf(true);
  };

  return (
    <Box sx={{ p: 4, width: "100%" }}>
      <DialogSave />

      {/* Page Header */}
      <Typography variant="h5" gutterBottom>
        Customer Orders Report
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Generate weekly, monthly, or custom order reports for the selected
        customer.
      </Typography>

      {!showPdf ? (
        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            {/* Report Type */}
            <Typography variant="subtitle1" gutterBottom>
              Report Type
            </Typography>

            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={handleReportTypeChange}
              sx={{ mb: 3 }}
            >
              <ToggleButton value="weekly">Weekly</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>

            <Divider sx={{ mb: 3 }} />

            {/* Date Range */}
            <Stack spacing={2}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={reportType !== "custom"}
                fullWidth
              />

              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={reportType !== "custom"}
                fullWidth
              />

              <Button
                variant="contained"
                size="large"
                onClick={generateReport}
                disabled={loading || !fromDate || !toDate}
              >
                {loading ? "Generating..." : "Generate Report"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ height: "80vh", border: "1px solid #ddd", mt: 3 }}>
          <PDFViewer width="100%" height="100%">
            <OrdersReportPdf
              orders={orders}
              fromDate={fromDate}
              toDate={toDate}
            />
          </PDFViewer>
        </Box>
      )}
    </Box>
  );
}
